import React, { useContext, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CAlert, CBadge, CButton, CCard, CCardBody, CCardHeader, CForm, CFormTextarea,
  CSpinner,
} from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { nameFromEmail } from '../components/format'
import { AuthContext } from '../App'

interface TaskRef {
  id: number
  title: string
}

interface Ask {
  id: number
  requested_by: string
  body: string
  status: 'pending' | 'answered' | 'failed'
  reply: string
  created_at: string
  answered_at: string | null
  tasks: TaskRef[]
}

/** Backend stores naive UTC; tag it so Date parses it as such. */
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const Ask: React.FC = () => {
  const queryClient = useQueryClient()
  const { auth } = useContext(AuthContext)
  const isAdmin = auth.user?.role === 'admin'
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  const asks = useQuery({
    queryKey: ['asks'],
    queryFn: () => api.get<Ask[]>('/v1/ask'),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((a) => a.status === 'pending') ? 3000 : false,
  })

  const submit = useMutation({
    mutationFn: () => api.post('/v1/ask', { body }),
    onSuccess: () => {
      setBody('')
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['asks'] })
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Something went wrong — try again.')
    },
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (body.trim()) submit.mutate()
  }

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/ask/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['asks'] }),
  })

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader className="d-flex align-items-center">
          <div>
            <h4 className="mb-0">🤖 Ask Arthur</h4>
            <div className="small text-body-secondary">
              Questions about the trip, ideas, or hand off flight info — Arthur answers
              here and puts follow-up work on the 📋 task board.
            </div>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={onSubmit}>
            <CFormTextarea
              rows={3}
              placeholder="Ask anything — e.g. “when do we land?” or paste flight details to hand off…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={4000}
            />
            <div className="d-flex align-items-center gap-2 mt-2">
              <CButton color="primary" type="submit" disabled={submit.isPending || !body.trim()}>
                {submit.isPending ? <CSpinner size="sm" /> : 'Ask Arthur'}
              </CButton>
              {error && <span className="small text-danger">{error}</span>}
            </div>
          </CForm>
        </CCardBody>
      </CCard>

      {asks.isLoading ? (
        <div className="text-center py-4"><CSpinner /></div>
      ) : asks.isError ? (
        <CAlert color="danger">Couldn't load the thread. Refresh the page.</CAlert>
      ) : (asks.data ?? []).length === 0 ? (
        <div className="text-center text-body-secondary py-4">
          Nothing asked yet — be the first.
        </div>
      ) : (
        (asks.data ?? []).map((ask) => (
          <CCard key={ask.id} className="mb-3">
            <CCardBody>
              <div className="d-flex align-items-center mb-1">
                <div className="small text-body-secondary">
                  {nameFromEmail(ask.requested_by) || ask.requested_by} · {fmtWhen(ask.created_at)}
                </div>
                {isAdmin && (
                  <CButton
                    size="sm" color="danger" variant="outline" className="ms-auto py-0"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm('Delete this question and its answer?')) {
                        remove.mutate(ask.id)
                      }
                    }}
                  >
                    Delete
                  </CButton>
                )}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{ask.body}</div>

              <div className="mt-2 ps-3 border-start">
                {ask.status === 'pending' && (
                  <div className="text-body-secondary small d-flex align-items-center gap-2">
                    <CSpinner size="sm" /> Arthur is thinking…
                  </div>
                )}
                {ask.status === 'failed' && (
                  <CAlert color="warning" className="py-2 mb-0">{ask.reply}</CAlert>
                )}
                {ask.status === 'answered' && (
                  <>
                    <div style={{ whiteSpace: 'pre-wrap' }}>🤖 {ask.reply}</div>
                    {ask.tasks.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {ask.tasks.map((t) => (
                          <Link
                            key={t.id}
                            to="/tasks"
                            className="text-decoration-none"
                          >
                            <CBadge color="primary" role="button">
                              📋 {t.title}
                            </CBadge>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </CCardBody>
          </CCard>
        ))
      )}
    </>
  )
}

export default Ask
